import { Suspense, lazy } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Providers } from '@/components/providers'
import { TelegramProvider } from '@/components/telegram/telegram-provider'
import { AuthGuard } from '@/components/auth/auth-guard'
import { RoleGuard } from '@/components/auth/role-guard'
import { ErrorBoundary } from '@/components/error-boundary'
import { Toaster } from '@/components/ui/sonner'
import { MockDataToggle } from '@/components/dev/mock-data-toggle'
import { DebugAuth } from '@/components/dev/debug-auth'
import { MockStorageViewer } from '@/components/dev/mock-storage-viewer'
import { ApiModeToggle } from '@/components/dev/api-mode-toggle'
import { SimpleLayout } from '@/components/layout/simple-layout'
import { PageGate } from '@/components/page-gate'
import { AdminLayout } from '@/components/layout/admin-layout'

// Route-level code splitting：頁面以 lazy 載入，由頂層 Suspense 顯示 fallback
const HomePage = lazy(() => import('@/pages/home'))
const DashboardPage = lazy(() => import('@/pages/dashboard'))
const CoursesPage = lazy(() => import('@/pages/courses'))
const CreateCoursePage = lazy(() => import('@/pages/courses/create'))
const EditCoursePage = lazy(() => import('@/pages/courses/edit'))
const CourseDetailPage = lazy(() => import('@/pages/courses/detail'))
// rednote/lookbook 展示頁與老師工具目前是寫死的假資料，
// 接上真實資料前先下架（見 docs/UX-AUDIT.md P2-1/P2-4）
const ComingSoonPage = lazy(() => import('@/pages/coming-soon'))
const StudentProfilePage = lazy(() => import('@/pages/student/profile'))
const AdminDashboardPage = lazy(() => import('@/pages/admin/dashboard'))
const AdminUsersPage = lazy(() => import('@/pages/admin/users'))
const AdminCreateUserPage = lazy(() => import('@/pages/admin/users/create'))
const AdminEditUserPage = lazy(() => import('@/pages/admin/users/edit'))
const AdminUserDetailPage = lazy(() => import('@/pages/admin/users/detail'))
const AdminStudentsPage = lazy(() => import('@/pages/admin/students'))
const AdminCreateStudentPage = lazy(() => import('@/pages/admin/students/create'))
const AdminEditStudentPage = lazy(() => import('@/pages/admin/students/edit'))
const AdminStudentDetailPage = lazy(() => import('@/pages/admin/students/detail'))
const AdminCoursesPage = lazy(() => import('@/pages/admin/courses'))
const AdminCombosPage = lazy(() => import('@/pages/admin/combos'))
const AdminTeachersPage = lazy(() => import('@/pages/admin/teachers'))
const AdminCreateTeacherPage = lazy(() => import('@/pages/admin/teachers/create'))
const AdminEditTeacherPage = lazy(() => import('@/pages/admin/teachers/edit'))
const AdminTeacherDetailPage = lazy(() => import('@/pages/admin/teachers/detail'))
const AdminAdministratorsPage = lazy(() => import('@/pages/admin/administrators'))
const AdminCreateAdministratorPage = lazy(() => import('@/pages/admin/administrators/create'))
const AdminEditAdministratorPage = lazy(() => import('@/pages/admin/administrators/edit'))
const AdminAdministratorDetailPage = lazy(() => import('@/pages/admin/administrators/detail'))
const AdminEnrollmentsPage = lazy(() => import('@/pages/admin/enrollments'))
const AdminCreateEnrollmentPage = lazy(() => import('@/pages/admin/enrollments/create'))
const AdminEditEnrollmentPage = lazy(() => import('@/pages/admin/enrollments/edit'))
const AdminEnrollmentDetailPage = lazy(() => import('@/pages/admin/enrollments/detail'))
const AdminCreateCoursePage = lazy(() => import('@/pages/admin/courses/create'))
const AdminEditCoursePage = lazy(() => import('@/pages/admin/courses/edit'))
const AdminCourseDetailPage = lazy(() => import('@/pages/admin/courses/detail'))
const AdminCreateComboPage = lazy(() => import('@/pages/admin/combos/create'))
const AdminEditComboPage = lazy(() => import('@/pages/admin/combos/edit'))
const AdminComboDetailPage = lazy(() => import('@/pages/admin/combos/detail'))
const AdminCourseEnrollmentCreatePage = lazy(() => import('@/pages/admin/courses/enrollments/create'))
const AdminCourseEnrollmentEditPage = lazy(() => import('@/pages/admin/courses/enrollments/edit'))
const LoginPage = lazy(() => import('@/pages/login'))
const PaymentResultPage = lazy(() => import('@/pages/payment/result'))
const NotFoundPage = lazy(() => import('@/pages/not-found'))
const MyEnrollmentsPage = lazy(() => import('@/pages/my-enrollments'))
const AdminAppearancePage = lazy(() => import('@/pages/admin/appearance'))

// /admin/* 路由統一套用角色守衛 + 後台版型
function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <RoleGuard roles={['admin']}>
      <AdminLayout>{children}</AdminLayout>
    </RoleGuard>
  )
}

function App() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    }>
      <ErrorBoundary>
      <TelegramProvider>
        <Providers>
          <AuthGuard>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/payment/result" element={<SimpleLayout><PaymentResultPage /></SimpleLayout>} />
              <Route path="/dashboard" element={<SimpleLayout><PageGate page="dashboard"><DashboardPage /></PageGate></SimpleLayout>} />
              <Route path="/courses" element={<SimpleLayout><PageGate page="courses"><CoursesPage /></PageGate></SimpleLayout>} />
              <Route path="/courses/create" element={<RoleGuard roles={['teacher', 'admin']}><SimpleLayout><CreateCoursePage /></SimpleLayout></RoleGuard>} />
              <Route path="/courses/:id" element={<SimpleLayout><PageGate page="courses"><CourseDetailPage /></PageGate></SimpleLayout>} />
              <Route path="/courses/:id/edit" element={<RoleGuard roles={['teacher', 'admin']}><SimpleLayout><EditCoursePage /></SimpleLayout></RoleGuard>} />
              <Route path="/courses/:id/rednote" element={<SimpleLayout><ComingSoonPage /></SimpleLayout>} />
              <Route path="/courses/:id/lookbook" element={<SimpleLayout><ComingSoonPage /></SimpleLayout>} />
              <Route path="/student/profile" element={<SimpleLayout><PageGate page="profile"><StudentProfilePage /></PageGate></SimpleLayout>} />
              <Route path="/my/enrollments" element={<SimpleLayout><PageGate page="enrollments"><MyEnrollmentsPage /></PageGate></SimpleLayout>} />
              <Route path="/teacher/profile" element={<SimpleLayout><ComingSoonPage /></SimpleLayout>} />
              <Route path="/teacher/enrollment" element={<SimpleLayout><ComingSoonPage /></SimpleLayout>} />
              <Route path="/teacher/lookbook" element={<SimpleLayout><ComingSoonPage /></SimpleLayout>} />
              <Route path="/teacher/kanban" element={<SimpleLayout><ComingSoonPage /></SimpleLayout>} />
              <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
              <Route path="/admin/appearance" element={<AdminRoute><AdminAppearancePage /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
              <Route path="/admin/users/create" element={<AdminRoute><AdminCreateUserPage /></AdminRoute>} />
              <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
              <Route path="/admin/users/:id/edit" element={<AdminRoute><AdminEditUserPage /></AdminRoute>} />
              <Route path="/admin/students" element={<AdminRoute><AdminStudentsPage /></AdminRoute>} />
              <Route path="/admin/students/create" element={<AdminRoute><AdminCreateStudentPage /></AdminRoute>} />
              <Route path="/admin/students/:id" element={<AdminRoute><AdminStudentDetailPage /></AdminRoute>} />
              <Route path="/admin/students/:id/edit" element={<AdminRoute><AdminEditStudentPage /></AdminRoute>} />
              <Route path="/admin/courses" element={<AdminRoute><AdminCoursesPage /></AdminRoute>} />
              <Route path="/admin/courses/create" element={<AdminRoute><AdminCreateCoursePage /></AdminRoute>} />
              <Route path="/admin/courses/:id" element={<AdminRoute><AdminCourseDetailPage /></AdminRoute>} />
              <Route path="/admin/courses/:id/edit" element={<AdminRoute><AdminEditCoursePage /></AdminRoute>} />
              <Route path="/admin/combos" element={<AdminRoute><AdminCombosPage /></AdminRoute>} />
              <Route path="/admin/teachers" element={<AdminRoute><AdminTeachersPage /></AdminRoute>} />
              <Route path="/admin/teachers/create" element={<AdminRoute><AdminCreateTeacherPage /></AdminRoute>} />
              <Route path="/admin/teachers/:id" element={<AdminRoute><AdminTeacherDetailPage /></AdminRoute>} />
              <Route path="/admin/teachers/:id/edit" element={<AdminRoute><AdminEditTeacherPage /></AdminRoute>} />
              <Route path="/admin/administrators" element={<AdminRoute><AdminAdministratorsPage /></AdminRoute>} />
              <Route path="/admin/administrators/create" element={<AdminRoute><AdminCreateAdministratorPage /></AdminRoute>} />
              <Route path="/admin/administrators/:id" element={<AdminRoute><AdminAdministratorDetailPage /></AdminRoute>} />
              <Route path="/admin/administrators/:id/edit" element={<AdminRoute><AdminEditAdministratorPage /></AdminRoute>} />
              <Route path="/admin/enrollments" element={<AdminRoute><AdminEnrollmentsPage /></AdminRoute>} />
              <Route path="/admin/enrollments/create" element={<AdminRoute><AdminCreateEnrollmentPage /></AdminRoute>} />
              <Route path="/admin/enrollments/:id" element={<AdminRoute><AdminEnrollmentDetailPage /></AdminRoute>} />
              <Route path="/admin/enrollments/:id/edit" element={<AdminRoute><AdminEditEnrollmentPage /></AdminRoute>} />
              <Route path="/admin/combos/create" element={<AdminRoute><AdminCreateComboPage /></AdminRoute>} />
              <Route path="/admin/combos/:id" element={<AdminRoute><AdminComboDetailPage /></AdminRoute>} />
              <Route path="/admin/combos/:id/edit" element={<AdminRoute><AdminEditComboPage /></AdminRoute>} />
              {/* Course-related enrollment routes */}
              <Route path="/admin/courses/:courseId/enrollments/create" element={<AdminRoute><AdminCourseEnrollmentCreatePage /></AdminRoute>} />
              <Route path="/admin/courses/:courseId/enrollments/:enrollmentId/edit" element={<AdminRoute><AdminCourseEnrollmentEditPage /></AdminRoute>} />
              {/* 未知路由：顯示 404 而不是白畫面 */}
              <Route path="*" element={<SimpleLayout><NotFoundPage /></SimpleLayout>} />
            </Routes>
            <Toaster />
            <ApiModeToggle />
            <MockDataToggle />
            <DebugAuth />
            <MockStorageViewer />
          </AuthGuard>
          {/* <ConsoleLogger /> */}
        </Providers>
      </TelegramProvider>
      </ErrorBoundary>
    </Suspense>
  )
}

export default App